export const VIDEO_FPS = 30;

export const BRAND = {
  navy: "#0A192F",
  blue: "#2563EB",
  blueDark: "#1D4ED8",
  ink: "#0F172A",
  muted: "#64748B",
  line: "#DCE7F5",
  surface: "#FFFFFF",
  softBlue: "#EFF6FF",
  green: "#059669",
  amber: "#D97706",
};

export const HERO_EXPLAINER_DURATION = 900;
export const LINKEDIN_DURATION = 360;
export const RECRUITING_DURATION = 450;
export const PROPOSAL_DURATION = 600;

export const DELIVERY_STEPS = [
  {
    label: "Scope",
    title: "Free discovery call",
    copy: "Clarify outcomes, timeline, team shape, and pilot scope.",
  },
  {
    label: "Pilot",
    title: "Paid two-week sprint",
    copy: "Start with a focused milestone before committing to a larger team.",
  },
  {
    label: "Team",
    title: "Vetted engineers join",
    copy: "Senior consultants and squads plug into your roadmap with US overlap.",
  },
  {
    label: "Rhythm",
    title: "Weekly demos and status",
    copy: "Clear ownership, visible progress, and practical delivery governance.",
  },
  {
    label: "Scale",
    title: "Grow or flex the team",
    copy: "Scale capacity up or down as product, data, and AI priorities shift.",
  },
];

export const HERO_STORY_SCENES = [
  {
    id: "pressure",
    eyebrow: "US delivery pressure",
    headline: "Your roadmap needs senior capacity before hiring catches up",
    body: "SaturnMax turns urgent product, AI, cloud, and data work into a staffed delivery path without a long recruiting cycle.",
    chapter: "Pressure",
    visualTitle: "Demand signals",
    visualSubtitle: "From signal to delivery path",
    visualItems: ["Hiring delay", "AI backlog", "Cloud and data roadmap"],
    proof: ["48 HR pilot kickoff", "3X cost leverage", "20+ consultants placed"],
  },
  {
    id: "scope",
    eyebrow: "Step 01 - Scope",
    headline: "A discovery call becomes a practical pilot brief",
    body: "We clarify outcomes, skills, timeline, overlap hours, and success criteria before you commit to a larger team.",
    chapter: "Scope",
    visualTitle: "Pilot brief",
    visualSubtitle: "Pilot plan in 48 hours",
    visualItems: ["Outcome", "Role mix", "Start date"],
    proof: ["Free discovery call", "Pilot plan", "Clear acceptance criteria"],
  },
  {
    id: "team",
    eyebrow: "Step 02 - Team",
    headline: "Vetted consultants and squads match the work",
    body: "Senior engineers are screened for the role, then embedded with US overlap and clear ownership from day one.",
    chapter: "Team",
    visualTitle: "Matched capacity",
    visualSubtitle: "Screened roles, ready to embed",
    visualItems: ["React + Python", "AI workflows", "AWS + data"],
    proof: ["Senior screening", "US overlap", "Flexible contracts"],
  },
  {
    id: "rhythm",
    eyebrow: "Step 03 - Ship",
    headline: "Weekly demos keep progress visible",
    body: "You get a simple operating cadence: demo, status, risks, next milestone, and decisions that unblock delivery.",
    chapter: "Rhythm",
    visualTitle: "Delivery cadence",
    visualSubtitle: "Demo cadence and risk visibility",
    visualItems: ["Monday plan", "Midweek build", "Friday demo"],
    proof: ["Written updates", "Demo cadence", "Delivery governance"],
  },
  {
    id: "scale",
    eyebrow: "Step 04 - Scale",
    headline: "Grow or flex the team after proof",
    body: "Start with a focused pilot, then expand into consultants, a dedicated squad, or AI/cloud/data delivery as demand grows.",
    chapter: "Scale",
    visualTitle: "Capacity options",
    visualSubtitle: "Capacity that grows after proof",
    visualItems: ["1 consultant", "2-5 engineer squad", "AI and cloud delivery"],
    proof: ["Scale up or down", "Cost-efficient model", "Long-term partner"],
  },
];

export const HERO_METRICS = [
  {
    metric: "48 HR",
    label: "Pilot kickoff",
  },
  {
    metric: "3X",
    label: "Cost leverage",
  },
  {
    metric: "20+",
    label: "Consultants placed",
  },
];

export const LINKEDIN_VARIANTS = {
  devTeams: {
    id: "SaturnMaxLinkedInDevTeams",
    title: "Dedicated dev squads for US teams",
    subtitle: "2-5 engineers embedded into your roadmap",
    points: ["React, Python, cloud, data", "US morning overlap", "Weekly demos"],
    cta: "Book a free discovery call",
  },
  aiAutomation: {
    id: "SaturnMaxLinkedInAIAutomation",
    title: "AI automation that ships",
    subtitle: "LLM workflows, RAG pipelines, and practical agents",
    points: ["Scoped pilot first", "Production-minded builds", "Cost-efficient delivery"],
    cta: "Build your AI pilot",
  },
  consultants: {
    id: "SaturnMaxLinkedInConsultants",
    title: "Vetted consultants on contract",
    subtitle: "Senior engineers for 3-12 month capacity needs",
    points: ["Fast matching", "Flexible contracts", "Clear ownership"],
    cta: "Scale with SaturnMax",
  },
};

export const SAMPLE_JOB = {
  role: "Senior Full Stack Consultant",
  location: "Remote USA overlap",
  type: "Contract",
  skills: ["React", "Python", "AWS", "AI workflows"],
  pitch: "Join US-aligned delivery squads solving real product and automation problems.",
  cta: "Share resume and contact details: info@saturnmax.com",
};

export const SAMPLE_PROPOSAL = {
  client: "US SaaS product team",
  challenge: "Support teams need faster answers without adding headcount.",
  solution: "A SaturnMax squad connects product docs, CRM data, and ticket history into an AI workflow.",
  outcomes: ["RAG assistant pilot", "Admin review workflow", "Weekly demo cadence"],
  cta: "Start with a scoped pilot",
};
